/**
 * The drawer's body for agent to-dos (the to-do action contract): ``agent.email_draft``,
 * ``agent.calendar_hold`` and ``agent.external``. It is the one open card every surface shows
 * (``OpenCard``): the to-do, one action block with the draft or the hold, Activity & Sources folded,
 * and the footer. The buttons come from the shared card list, so the drawer says what the tray says:
 * at Draft an email's main button is Open in Gmail, and editing it turns into Save draft.
 * Approvals always carry the run id, so the server binds the yes to that run's exact operation hash.
 */

import type { ComponentType, ReactNode } from "react";
import { ArrowLeftIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { OpenCard } from "@/components/todos/card/open-card";
import { useCardDispatch } from "@/components/todos/card/use-card-dispatch";
import { type TodoDTO, useTodoDetail } from "@/hooks/use-agent-todos";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSafeNavigate } from "@/hooks/use-safe-navigate";
import { formatDueLabel } from "@/lib/todo-due-tier";
import { cn } from "@/lib/utils";

type Shell = ComponentType<{ className?: string; children?: ReactNode }>;

interface PanelProps {
  todo: TodoDTO;
  kind: "email" | "hold" | "external";
  Title: Shell;
  Description: Shell;
  onClose: () => void;
  /** What the opener's row showed beyond the to-do (``TodoDrawerProps.aside``). */
  aside?: ReactNode;
}

const noOpen = () => {};

export function AgentTodoPanel({ todo, kind, Title, Description, onClose, aside }: PanelProps) {
  const isMobile = useIsMobile();
  const { data: detail } = useTodoDetail(todo.id);
  const live = detail?.todo ?? todo;
  const dispatch = useCardDispatch(noOpen);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={cn("px-5 pt-4", isMobile ? "pr-5" : "pr-12")}>
        {isMobile && (
          <Button variant="ghost" size="sm" className="-ml-2 min-h-11 gap-1.5" onClick={onClose}>
            <ArrowLeftIcon className="size-4" aria-hidden />
            Back to To-dos
          </Button>
        )}
        <Title className="sr-only">{live.title}</Title>
        <Description className="sr-only">{live.subtitle || "To-do details."}</Description>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="@container px-5 pb-5 pt-1">
          {aside && <div className="mb-3 empty:hidden">{aside}</div>}
          <OpenCard
            todo={live}
            dispatch={dispatch}
            notice={kind === "hold" && live.next_action === "approve_hold" ? <FindAnotherTime todo={live} /> : undefined}
          />
        </div>
      </ScrollArea>
    </div>
  );
}

/** Hands the hold back to Kory chat to propose other times. */
function FindAnotherTime({ todo }: { todo: TodoDTO }) {
  const navigate = useSafeNavigate();
  if (!navigate) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      className="min-h-11 w-fit @lg:min-h-8"
      onClick={() => navigate("/kory", { state: { seedPrompt: todoSeedPrompt(todo, "Find me another time for this hold.") } })}
    >
      Find Another Time
    </Button>
  );
}

export function todoSeedPrompt(todo: TodoDTO, ask: string): string {
  const due = formatDueLabel(todo.due_at);
  return `About my to-do “${todo.title}”${due ? ` (due ${due})` : ""}: ${ask}\n\nTo-do reference: ${todo.id}`;
}
